import { Alert, Checkbox, Input, InputNumber, Select, Space } from 'antd';
import {
  type ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useOptionalActiveSession } from '../api/active/sessionProvider';
import type { QuoteFieldDefinition } from '../settings-integration/settingsApi';
import { createSettingsApi } from '../settings-integration/settingsApi';
import { canReadFieldMetadata } from './fieldMetadataAccess';
import { subscribeFieldDisplayNamesInvalidation } from './useFieldDisplayNames';

export type BusinessFieldGroup = 'package' | 'plan' | 'customer';
export type BusinessFieldValue = {
  definitionId: string;
  definitionVersion: number;
  value: unknown;
};

export function fieldValuesFromCustom(
  custom: Record<string, unknown> | undefined,
): BusinessFieldValue[] {
  const values = custom?.fieldValues;
  if (!Array.isArray(values)) return [];
  return values.filter(
    (entry): entry is BusinessFieldValue =>
      typeof entry === 'object' &&
      entry !== null &&
      typeof entry.definitionId === 'string' &&
      Number.isInteger(entry.definitionVersion) &&
      'value' in entry,
  );
}

export function customWithFieldValues(
  custom: Record<string, unknown> | undefined,
  fieldValues: BusinessFieldValue[],
): Record<string, unknown> {
  if (fieldValues.length === 0 && !Array.isArray(custom?.fieldValues))
    return { ...custom };
  return { ...custom, fieldValues };
}

export function formatBusinessFieldValue(
  definition: QuoteFieldDefinition,
  value: unknown,
): string {
  if (value === null || value === undefined || value === '') return '-';
  if (definition.valueType === 'money' && typeof value === 'number')
    return `¥${(value / 100).toFixed(2)}`;
  if (typeof value === 'boolean') return value ? '是' : '否';
  if (definition.valueType === 'single_select' && typeof value === 'string')
    return (
      definition.options?.find((option) => option.value === value)
        ?.displayLabel ?? value
    );
  if (definition.valueType === 'multi_select' && Array.isArray(value))
    return value
      .map(
        (entry) =>
          definition.options?.find((option) => option.value === entry)
            ?.displayLabel ?? String(entry),
      )
      .join('、');
  if (Array.isArray(value)) return value.join('、');
  return typeof value === 'string' || typeof value === 'number'
    ? String(value)
    : '-';
}

export function useBusinessFields(groupKey: BusinessFieldGroup) {
  const session = useOptionalActiveSession();
  const ready = session?.state.status === 'ready' ? session.state : null;
  const transport = session?.transport;
  const canRead = Boolean(ready && canReadFieldMetadata(ready.user, groupKey));
  const [definitions, setDefinitions] = useState<QuoteFieldDefinition[]>([]);
  const [error, setError] = useState('');
  const generation = useRef(0);
  const reload = useCallback(async () => {
    const current = ++generation.current;
    if (!ready || !transport || !canRead) {
      setDefinitions([]);
      setError('');
      return;
    }
    try {
      const result = await createSettingsApi(
        transport,
        ready.csrfToken,
      ).listDefinitions();
      if (current !== generation.current) return;
      setDefinitions(
        result.items.filter(
          (field) =>
            field.groupKey === groupKey &&
            field.sourceKind === 'manual' &&
            field.securityClass === 'ordinary' &&
            field.enabled,
        ),
      );
      setError('');
    } catch (cause) {
      if (current !== generation.current) return;
      setError(cause instanceof Error ? cause.message : '字段读取失败');
    }
  }, [canRead, groupKey, ready, transport]);
  useEffect(() => {
    void reload();
    const unsubscribe = subscribeFieldDisplayNamesInvalidation(() => {
      void reload();
    });
    return () => {
      generation.current++;
      unsubscribe();
    };
  }, [reload]);
  return { definitions, error, reload };
}

export function BusinessCustomFields({
  definitions,
  values,
  onChange,
  disabled = false,
}: {
  definitions: readonly QuoteFieldDefinition[];
  values: readonly BusinessFieldValue[];
  onChange(values: BusinessFieldValue[]): void;
  disabled?: boolean;
}) {
  if (definitions.length === 0) return null;
  const change = (definition: QuoteFieldDefinition, value: unknown) => {
    const next = values.filter((item) => item.definitionId !== definition.id);
    next.push({
      definitionId: definition.id,
      definitionVersion: definition.definitionVersion,
      value,
    });
    onChange(next);
  };
  return (
    <section aria-label="自定义字段">
      <h3>自定义字段</h3>
      <Space orientation="vertical" style={{ width: '100%' }}>
        {definitions.map((definition) => {
          const value = values.find(
            (item) => item.definitionId === definition.id,
          )?.value;
          let control: ReactNode;
          switch (definition.valueType) {
            case 'number':
            case 'money':
            case 'percent':
              control = (
                <InputNumber
                  aria-label={definition.labelZh}
                  style={{ width: '100%' }}
                  disabled={disabled}
                  precision={definition.valueType === 'number' ? undefined : 2}
                  value={
                    typeof value === 'number'
                      ? definition.valueType === 'money'
                        ? value / 100
                        : value
                      : null
                  }
                  onChange={(next) =>
                    change(
                      definition,
                      next === null
                        ? null
                        : definition.valueType === 'money'
                          ? Math.round(next * 100)
                          : next,
                    )
                  }
                />
              );
              break;
            case 'boolean':
              control = (
                <Checkbox
                  aria-label={definition.labelZh}
                  disabled={disabled}
                  checked={value === true}
                  onChange={(event) => change(definition, event.target.checked)}
                />
              );
              break;
            case 'single_select':
            case 'multi_select':
              control = (
                <Select
                  aria-label={definition.labelZh}
                  style={{ width: '100%' }}
                  disabled={disabled}
                  mode={
                    definition.valueType === 'multi_select'
                      ? 'multiple'
                      : undefined
                  }
                  value={
                    definition.valueType === 'multi_select'
                      ? Array.isArray(value)
                        ? value
                        : []
                      : typeof value === 'string'
                        ? value
                        : undefined
                  }
                  options={(definition.options ?? []).map((option) => ({
                    value: option.value,
                    label: option.displayLabel,
                    disabled: !option.enabled,
                  }))}
                  onChange={(next) => change(definition, next)}
                />
              );
              break;
            case 'date':
              control = (
                <Input
                  aria-label={definition.labelZh}
                  type="date"
                  disabled={disabled}
                  value={typeof value === 'string' ? value : ''}
                  onChange={(event) =>
                    change(definition, event.target.value || null)
                  }
                />
              );
              break;
            case 'image':
              control = (
                <Alert
                  type="info"
                  title="图片字段请在设置中维护定义；当前对象尚不支持上传此类字段值"
                />
              );
              break;
            default:
              control = (
                <Input
                  aria-label={definition.labelZh}
                  disabled={disabled}
                  value={typeof value === 'string' ? value : ''}
                  onChange={(event) =>
                    change(definition, event.target.value || null)
                  }
                />
              );
          }
          return (
            <div key={definition.id} style={{ width: '100%' }}>
              <div style={{ marginBottom: 4 }}>{definition.labelZh}</div>
              {control}
            </div>
          );
        })}
      </Space>
    </section>
  );
}
